import { useCallback } from 'react';
import { parseCDXML } from '@alchemiesolns/cdxml-parser';
import { toConfigString } from '@alchemiesolns/workspace-serialization';
import { extractPNGMetadata, extractSVGMetadata, type ExportMetadata } from '@alchemiesolns/image-metadata';

export interface AlchemieImportResult {
  data: Record<string, unknown>;
  meta: ExportMetadata;
  sourceFilename?: string;
}

interface UseFileImportOptions {
  /** Called when a single molecule is imported via CDXML */
  onConfigImport?: (configString: string) => void;
  /** Called when a full scheme is imported from an enhanced PNG/SVG */
  onAlchemieImport?: (result: AlchemieImportResult) => void;
  /** Called when an import fails */
  onImportError?: (message: string) => void;
}

/** Check if a string looks like CDXML content */
function isCDXML(text: string): boolean {
  const trimmed = text.trim();
  return trimmed.includes('<CDXML') || (trimmed.startsWith('<?xml') && trimmed.includes('<CDXML'));
}

/**
 * Hook for handling file imports (CDXML, PNG, SVG) in molecule panels.
 * Extracts file processing logic for reuse across components.
 */
export function useFileImport({
  onConfigImport,
  onAlchemieImport,
  onImportError,
}: UseFileImportOptions) {
  const processCDXML = useCallback((xmlString: string) => {
    if (!onConfigImport) return;
    const result = parseCDXML(xmlString);
    if (result.ok) {
      onConfigImport(toConfigString(result.workspace));
    } else {
      onImportError?.(result.error.message);
    }
  }, [onConfigImport, onImportError]);

  /** Process a PNG file — extract alchemieData or fall through */
  const processPNG = useCallback(async (file: File): Promise<boolean> => {
    if (!onAlchemieImport) return false;
    const buffer = new Uint8Array(await file.arrayBuffer());
    const meta = extractPNGMetadata(buffer);
    if (meta.alchemieData) {
      onAlchemieImport({ data: meta.alchemieData, meta, sourceFilename: file.name });
      return true;
    }
    return false;
  }, [onAlchemieImport]);

  /** Process an SVG file — try alchemieData first */
  const processSVG = useCallback(async (file: File): Promise<boolean> => {
    if (!onAlchemieImport) return false;
    const text = await file.text();
    const meta = extractSVGMetadata(text);
    if (meta.alchemieData) {
      onAlchemieImport({ data: meta.alchemieData, meta, sourceFilename: file.name });
      return true;
    }
    return false;
  }, [onAlchemieImport]);

  /** Process a dropped or selected file */
  const processFile = useCallback(async (file: File): Promise<void> => {
    const name = file.name.toLowerCase();

    // Enhanced PNG — extract full scheme from metadata
    if (name.endsWith('.png') || file.type === 'image/png') {
      const handled = await processPNG(file);
      if (handled) return;
      // No alchemie metadata — it's just a plain image, ignore
      return;
    }

    // SVG — try alchemie metadata first
    if (name.endsWith('.svg') || file.type === 'image/svg+xml') {
      const handled = await processSVG(file);
      if (handled) return;
      // No alchemie metadata — ignore
      return;
    }

    // CDXML files
    if (name.endsWith('.cdxml') || name.endsWith('.xml') || file.type === 'text/xml' || file.type === 'application/xml') {
      if (onConfigImport) {
        const text = await file.text();
        processCDXML(text);
      }
      return;
    }

    // Unsupported file type
    onImportError?.('Unsupported file type. Please use CDXML, PNG, or SVG files.');
  }, [onConfigImport, processCDXML, processPNG, processSVG, onImportError]);

  /** Open a file picker dialog */
  const browseFiles = useCallback(() => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.cdxml,.xml,.png,.svg';
    input.onchange = async (e) => {
      const file = (e.target as HTMLInputElement).files?.[0];
      if (file) {
        await processFile(file);
      }
    };
    input.click();
  }, [processFile]);

  return {
    processCDXML,
    processFile,
    browseFiles,
    isCDXML,
  };
}
